// src/components/Stream/LivestreamMusicPlayer.jsx
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box, CircularProgress, IconButton, Typography, Tooltip } from "@mui/material";
import SkipPreviousRoundedIcon from "@mui/icons-material/SkipPreviousRounded";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import KeyboardArrowUpRoundedIcon from "@mui/icons-material/KeyboardArrowUpRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { start } from "../../action/streamSong";
import ApiService from "../../services/api-service";
import { getUserId } from "../../constants/function";
import emitter from "../../EventEmitter";
import AddMusicToPlaylist from "../AddMusicToPlaylist";
import notifier from "../../components/Notifier";
import { getPlayableUrl } from "../../utils/songUrl";
import { currentPlayableUrl } from "../../hooks/usePlayableUrl";
import { ARTWORK_FALLBACK, artworkUrlOf } from "../../utils/songArtwork";
import { HLS_AUDIENCE_DELAY_SEC } from "../../constants/liveMusic";
import { useHostMusicAudio } from "./useHostMusicAudio";
import { carriesPlaylist, hostPositionForNewSong, planAudienceSwap } from "./audienceSwap";
import { DEFAULT_MUSIC_TIMING, expectedHostPositionSec, hostStateAt, hostStateFrom, hostStatesFromTimeline, mergeHostStates, rememberHostState } from "./frameClock";

const FRAME_CLOCK_TICK_MS = 250;
// No host state for this long while the clock runs means updates were
// missed (a dropped socket); the server sends the recent ones again.
const HOST_STATE_GAP_MS = 8000;
const TIMELINE_REQUEST_EVERY_MS = 10000;

// A cover that fails to load shows the music-note tile instead of a broken image.
const showArtworkFallback = (event) => {
  if (event.currentTarget.src !== ARTWORK_FALLBACK) event.currentTarget.src = ARTWORK_FALLBACK;
};

// Host music volume (0-1) a viewer starts at before the first state broadcast.
const DEFAULT_HOST_MUSIC_VOLUME = 0.25;

export default function LivestreamMusicPlayer({ isHost, canControl = false, roomId, streamData, playHostMusicLocally = false, hostMusicMuted = true, audienceDelaySec = null, frameClockRef = null, hostMusicTimeline = null, musicTiming = DEFAULT_MUSIC_TIMING, publishMusicTrack, stopMusicTrack, socket, remoteUsers, client }) {
  const dispatch = useDispatch();
  const { index, songs, track, vol, pos } = useSelector((state) => state.streamSong);
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const [showSongPicker, setShowSongPicker] = useState(false);
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const barsRef = useRef(null); // Store bars so they don't regenerate
  const progressRef = useRef(0); // Store current progress for canvas access
  const audioVolumeRef = useRef(0); // Track real-time audio volume (host or audience from Agora stream)
  const remoteAudioTrackRef = useRef(null); // Track remote audio track for audience
  const remoteUsersRef = useRef(remoteUsers);
  const songsRef = useRef(songs);
  const indexRef = useRef(index); // Track current index for socket handlers
  const volRef = useRef(vol); // Track volume for interval callback without re-creating interval
  const audienceSongIdRef = useRef(null); // Track which song the viewer is currently on
  // Where the viewer's current song started, set in the same tick as the
  // swap so the player reads it in the render that changes the song.
  const heardStartRef = useRef({ songId: null, startSec: 0 });
  // Audience HLS sync: the audience hears the stream ~15s behind the host (nginx-rtmp
  // segments + player buffering; matches backend HLS_PIPELINE_DELAY_S). The host
  // broadcasts its OWN clock, so without compensation the audience progress bar (and
  // song swap) runs ~15s ahead of the audio. We DELAY the audience song swap until the
  // audio actually arrives, then start its progress from 0 so the bar tracks the audio.
  const pendingSwapTimerRef = useRef(null); // setTimeout handle for the deferred audience swap
  const pendingSwapSongIdRef = useRef(null); // song id a deferred swap is already scheduled for

  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [audiencePlaying, setAudiencePlaying] = useState(false); // Track if audience should show as playing
  const [hostMusicVolume, setHostMusicVolume] = useState(DEFAULT_HOST_MUSIC_VOLUME);
  const playHostMusicLocallyRef = useRef(playHostMusicLocally);
  playHostMusicLocallyRef.current = playHostMusicLocally;
  const scheduledPlayingRef = useRef(null); // last play state queued for the audience
  // A direct OBS stream's delay is measured from the viewer's own video; the
  // Agora pipeline's extra hops aren't visible to the player, so it keeps 15 s.
  const audienceDelayRef = useRef(HLS_AUDIENCE_DELAY_SEC);
  audienceDelayRef.current = playHostMusicLocally && audienceDelaySec ? audienceDelaySec : HLS_AUDIENCE_DELAY_SEC;
  const playingTimersRef = useRef([]);
  // Direct OBS streams: the host's stamped states, and whether the frame
  // clock (from the stamped playlist) drives the music instead of a delay.
  const hostStatesRef = useRef([]);
  const clockActiveRef = useRef(false);
  const syncTargetRef = useRef(null);
  const clockMotionRef = useRef({ frameMs: null, movedAtPerf: 0 });
  // Once the clock has driven the music it keeps control for the stream;
  // handing back to the fixed delay reloaded and jumped the song.
  const clockEverActiveRef = useRef(false);
  const lastHostStateAtRef = useRef(0);
  const lastTimelineRequestAtRef = useRef(0);
  const musicTimingRef = useRef(musicTiming);
  musicTimingRef.current = musicTiming;
  // A joining viewer's picture is 10-20 s behind the host; the server's recent
  // states let the frame clock take over from the first frame, not after a guess.
  useEffect(() => {
    if (!hostMusicTimeline) return;
    hostStatesRef.current = mergeHostStates(hostStatesRef.current, hostStatesFromTimeline(hostMusicTimeline));
  }, [hostMusicTimeline]);
  const [trackState, setTrackState] = useState(null); // Track state for re-rendering when play/pause
  const playPauseInProgress = useRef(false); // Prevent double-click
  const streamingActiveRef = useRef(null); // musicId of active server-side stream session
  const nextSongInProgress = useRef(false); // Prevent double handleNextSong calls

  // Check if there's a valid track or at least songs available
  const hasTrack = !!track && index >= 0 && songs[index];
  const hasSongs = songs && songs.length > 0;
  // Always use Redux index, fallback to 0 if invalid
  const displayIndex = (index >= 0 && index < songs.length) ? index : 0;
  const currentSong = hasSongs ? songs[displayIndex] : null;
  // For audience, duration comes from the song metadata
  const duration = hasTrack ? Math.floor(track.duration) || 0 : (currentSong?.duration || currentSong?.fileId?.duration || 180);
  // Use trackState for reactive updates when track.currentState changes
  const isPlaying = hasTrack && (trackState === "playing" || track.currentState === "playing");
  const currentSongId = currentSong?._id || null;

  // For audience, show as playing based on audiencePlaying state
  const showAsPlaying = isHost ? isPlaying : audiencePlaying;

  // Until the deferred swap lands, songs[index] is not what the viewer
  // should hear yet.
  const heardSong = currentSong && audienceSongIdRef.current === currentSong._id ? currentSong : null;
  useHostMusicAudio({
    enabled: playHostMusicLocally && !isHost,
    song: heardSong,
    startSec: heardStartRef.current.songId === heardSong?._id ? heardStartRef.current.startSec : 0,
    playing: audiencePlaying,
    hostVolume: hostMusicVolume,
    muted: hostMusicMuted,
    syncTargetRef,
    timing: musicTiming,
  });

  // The host owns the audio track and drives playback directly. A moderator's
  // buttons ask over the socket, the host's relay carries it out, and the
  // host's state broadcast updates every screen including the moderator's.
  const canDrive = isHost || canControl;

  const sendControl = (action, extra = {}) => {
    if (!socket || !roomId) return;
    socket.emit('music-control', { roomId, action, ...extra });
  };

  // The relay handler below is registered once, so it reads the latest
  // handlers through a ref instead of a stale closure.
  const controlHandlersRef = useRef({});
  // handlePickSong is a const declared further down, so it is attached there.
  controlHandlersRef.current = { ...controlHandlersRef.current, handlePlayPause, handleNextSong, handlePrevSong };
  useEffect(() => {
    if (!socket || !roomId || !isHost) return;
    const handleMusicControl = (data) => {
      if (!data || data.roomId !== roomId) return;
      const h = controlHandlersRef.current;
      switch (data.action) {
        case 'toggle': h.handlePlayPause?.(); break;
        case 'play': if (!isPlayingRef.current) h.handlePlayPause?.(); break;
        case 'pause': if (isPlayingRef.current) h.handlePlayPause?.(); break;
        case 'next': h.handleNextSong?.(); break;
        case 'prev': h.handlePrevSong?.(); break;
        case 'pick': if (Number.isInteger(data.index)) h.handlePickSong?.(data.index); break;
        default: break;
      }
    };
    socket.on('music-control', handleMusicControl);
    return () => { socket.off('music-control', handleMusicControl); };
  }, [socket, roomId, isHost]);
  const isPlayingRef = useRef(false);
  isPlayingRef.current = isPlaying;

  useEffect(() => {
    remoteUsersRef.current = remoteUsers;
  }, [remoteUsers]);

  useEffect(() => {
    songsRef.current = songs;
  }, [songs]);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  useEffect(() => {
    volRef.current = vol;
  }, [vol]);

  /************************************************************************
   * Helper function to emit song-start event for listen count tracking
   * This event tells the backend when songs start/stop/change
   ************************************************************************/
  const emitSongStartEvent = (type, song = currentSong, currentProgress = progressRef.current) => {
    if (!socket || !isHost || !song) return;

    const songData = {
      _id: song._id,
      title: song.title,
      artist: song.uploader?.displayArtistName || song.artist || 'Unknown',
      duration: (song.duration || song.fileId?.duration || 180) * 1000, // Convert to milliseconds
      thumbnailImg: song.thumbnailImg,
      roomId: roomId,
      ownerId: getUserId(),
      type: type, // "start" | "pause" | "stop" | "next" | "previous"
      pos: currentProgress,
      timestamp: Date.now()
    };

    socket.emit("song-start", songData);
  };

  /************************************************************************
   * Server-side stream counting (STREAM_START / STREAM_STOP)
   * Tracks listens, balance deduction, and playlist/album counts
   ************************************************************************/
  const handleStartStreaming = async (song) => {
    if (!isHost || !song?._id || streamingActiveRef.current === song._id) return;
    streamingActiveRef.current = song._id;
    streamTrackingRef.current = { started: true, songId: song._id };
    try {
      await ApiService.STREAM_START({
        musicId: song._id,
        playlistId: roomId || undefined,
        livestreamId: roomId || undefined,
      });
    } catch (err) {
      console.error('[MusicPlayer] STREAM_START failed:', err);
    }
  };

  const handleStopStreaming = async (song = currentSong) => {
    if (!isHost || !song?._id || streamingActiveRef.current !== song._id) return;
    streamingActiveRef.current = null;
    streamTrackingRef.current = { started: false, songId: null };
    try {
      await ApiService.STREAM_STOP({ musicId: song._id });
    } catch (err) {
      console.error('[MusicPlayer] STREAM_STOP failed:', err);
    }
  };

  // Cleanup: stop active stream on unmount
  useEffect(() => {
    return () => {
      if (streamingActiveRef.current) {
        ApiService.STREAM_STOP({ musicId: streamingActiveRef.current }).catch(() => {});
      }
    };
  }, []);

  // Stream tracking helper — called from progress polling interval
  const streamTrackingRef = useRef({ started: false, songId: null });

  // Override track.onEnded — streamPlayer.js also sets onEnded but doesn't call
  // STREAM_STOP. Since LivestreamMusicPlayer renders after StreamPlayer in the
  // React tree, this effect fires last and our handler wins.
  useEffect(() => {
    if (!hasTrack || !isHost) return;

    const songAtEnd = currentSong;
    track.onEnded = () => {
      // Fire STREAM_STOP for the song that just ended
      if (streamingActiveRef.current === songAtEnd?._id) {
        console.log('[MusicPlayer] onEnded: firing STREAM_STOP for', songAtEnd?.title);
        ApiService.STREAM_STOP({ musicId: songAtEnd._id }).catch(() => {});
        streamingActiveRef.current = null;
        streamTrackingRef.current = { started: false, songId: null };
      }
      // Advance to next song if not at end of playlist
      if (displayIndex < songs.length - 1) {
        handleNextSong();
      }
    };
  }, [hasTrack, track, isHost, currentSong, displayIndex, songs.length]);

  // Safety net: ensure STREAM_STOP fires when song transitions happen externally
  // (e.g., streamPlayer.js dispatches {track: null} via its own handleNextSong)
  useEffect(() => {
    if (!isHost) return;
    const activeMusicId = streamingActiveRef.current;
    if (activeMusicId && activeMusicId !== currentSongId) {
      console.log('[MusicPlayer] Safety net: firing STREAM_STOP for', activeMusicId, '(song changed to', currentSongId, ')');
      ApiService.STREAM_STOP({ musicId: activeMusicId }).catch(() => {});
      streamingActiveRef.current = null;
      streamTrackingRef.current = { started: false, songId: null };
    }
  }, [currentSongId, isHost]);

  // Track state watcher: fire STREAM_STOP/START when track play state changes,
  // regardless of which component triggered it (handles streamPlayer.js pause/play)
  const prevPlayingRef = useRef(false);
  useEffect(() => {
    if (!isHost) return;
    const currentlyPlaying = hasTrack && track?.currentState === "playing";

    if (prevPlayingRef.current && !currentlyPlaying && streamingActiveRef.current) {
      // Was playing, now paused/stopped → fire STREAM_STOP
      console.log('[MusicPlayer] Track state watcher: playing → stopped. Firing STREAM_STOP for', streamingActiveRef.current);
      ApiService.STREAM_STOP({ musicId: streamingActiveRef.current }).catch(() => {});
      streamingActiveRef.current = null;
      streamTrackingRef.current = { started: false, songId: null };
    } else if (!prevPlayingRef.current && currentlyPlaying && !streamingActiveRef.current && currentSong?._id) {
      // Was paused/stopped, now playing → fire STREAM_START
      handleStartStreaming(currentSong);
    }

    prevPlayingRef.current = currentlyPlaying;
  }, [hasTrack, track?.currentState, isHost, currentSong]);

  /************************************************************************
   * Socket synchronization - Host broadcasts state periodically, Audience receives
   ************************************************************************/
  useEffect(() => {
    if (!socket || !roomId) return;

    // Cancel any pending (deferred) audience song swap.
    const cancelPendingSwap = () => {
      if (pendingSwapTimerRef.current) {
        clearTimeout(pendingSwapTimerRef.current);
        pendingSwapTimerRef.current = null;
      }
      pendingSwapSongIdRef.current = null;
    };

    // Defer the audience song swap until the audio for the new song actually
    // reaches the viewer (~HLS_AUDIENCE_DELAY_SEC after the host started it).
    // hostProgressSec is how far into the song the host already is when we first
    // see the change, so the remaining wait is the delay minus that. applySwap()
    // performs the real UI swap; we start the audience progress from 0 so the bar
    // tracks the audio rather than running ahead of it.
    const scheduleAudienceSongSwap = (songId, hostProgressSec, applySwap) => {
      if (pendingSwapSongIdRef.current === songId) return; // already scheduled
      cancelPendingSwap();
      pendingSwapSongIdRef.current = songId;
      // Fresh song start: host ~0s in -> wait the full HLS delay, then seed 0.
      // Late join mid-song: host already >delay in -> swap now, seed the position
      // the audience actually HEARS (host position at swap time minus the delay).
      const { waitSec, seedSec } = planAudienceSwap({ hostPosSec: hostProgressSec, delaySec: audienceDelayRef.current });
      pendingSwapTimerRef.current = setTimeout(() => {
        pendingSwapTimerRef.current = null;
        pendingSwapSongIdRef.current = null;
        audienceSongIdRef.current = songId;
        heardStartRef.current = { songId, startSec: seedSec };
        progressRef.current = seedSec;
        setProgress(seedSec);
        applySwap(seedSec);
      }, waitSec * 1000);
    };

    // Listen for mobile app music state updates (both host and audience)
    const handleMobileMusicState = (data) => {
      if (!data.isLivestream) return;

      // For host viewing on web: only sync from their own mobile session
      // For audience: accept all events (they come from the host)
      if (isHost && data.userId !== getUserId()) return;

      // Use ref to get current songs (avoids stale closure)
      const currentSongs = songsRef.current;

      // Find the song in our current playlist by musicId
      const songIdx = currentSongs.findIndex(s => s._id === data.musicId);

      if (songIdx !== -1) {
        const indexChanged = songIdx !== indexRef.current;

        if (isHost) {
          // Host mirrors its own mobile position immediately (no HLS delay for
          // the host — they hear their own playback in real time).
          progressRef.current = data.position;
          setProgress(data.position);
          if (indexChanged) {
            dispatch(
              start({ index: songIdx, songs: currentSongs, pos: data.position, track: null, vol: vol || 25 })
            );
          }
        } else {
          // AUDIENCE: defer the song swap until the audio arrives (HLS delay),
          // then start its progress from 0 so the bar tracks what's heard.
          const isNewSong =
            data.musicId !== audienceSongIdRef.current &&
            pendingSwapSongIdRef.current !== data.musicId;
          if (isNewSong) {
            scheduleAudienceSongSwap(data.musicId, data.position || 0, (seedSec) => {
              dispatch(
                start({ index: songIdx, songs: currentSongs, pos: seedSec, track: null, vol: vol || 25 })
              );
            });
          } else if (data.musicId === audienceSongIdRef.current && typeof data.position === 'number') {
            // Same (already-swapped) song — drift-correct against the HEARD
            // position (host position minus the HLS delay), not the host clock.
            const heard = Math.max(0, data.position - audienceDelayRef.current);
            const drift = Math.abs(progressRef.current - heard);
            if (drift > 3) {
              progressRef.current = heard;
              setProgress(heard);
            }
          }
        }

        // Update playing state
        if (data.isPlaying !== undefined) {
          setAudiencePlaying(data.isPlaying);
        }
      }
    };

    socket.on('mobile-music-state', handleMobileMusicState);

    // AUDIENCE: Listen for music state updates from host
    if (!isHost) {
      const handleMusicStateUpdate = (data) => {
        if (data.roomId !== roomId) return;

        const { musicState } = data;
        if (!carriesPlaylist(musicState)) return;
        if (playHostMusicLocallyRef.current && typeof data.serverTime === "number") {
          lastHostStateAtRef.current = performance.now();
          hostStatesRef.current = rememberHostState(hostStatesRef.current, hostStateFrom(musicState, data.serverTime));
          // The frame clock replays these states; the delay path below only
          // runs until the clock first ticks.
          if (clockActiveRef.current || (clockEverActiveRef.current && musicTimingRef.current.useFrameClock)) return;
        }

        // Update playing state for audience
        // Note: Volume is now read directly from Agora remote audio track, not from socket
        if (playHostMusicLocallyRef.current) {
          // The viewer plays the music, so a pause or resume has to wait for
          // the video to show it. The first state applies at once: the song
          // swap below already seeds the delayed position.
          const isPlayingNow = musicState.isPlaying === true;
          if (typeof musicState.currentVolume === "number") setHostMusicVolume(musicState.currentVolume);
          if (scheduledPlayingRef.current === null) {
            scheduledPlayingRef.current = isPlayingNow;
            setAudiencePlaying(isPlayingNow);
          } else if (scheduledPlayingRef.current !== isPlayingNow) {
            scheduledPlayingRef.current = isPlayingNow;
            playingTimersRef.current.push(
              setTimeout(() => setAudiencePlaying(isPlayingNow), audienceDelayRef.current * 1000)
            );
          }
        } else {
          setAudiencePlaying(musicState.isPlaying);
        }

        // Detect song change: a new current song must be DEFERRED for the
        // audience until its audio arrives (~HLS delay behind the host).
        const incomingSongId = musicState.songs?.[musicState.index]?._id;
        const isNewSong =
          incomingSongId &&
          incomingSongId !== audienceSongIdRef.current &&
          pendingSwapSongIdRef.current !== incomingSongId;

        const prevSongIds = songsRef.current.map(s => s._id).join(',');
        const newSongIds = (musicState.songs || []).map(s => s._id).join(',');
        const songsChanged = prevSongIds !== newSongIds;
        const indexChanged = musicState.index !== indexRef.current;

        if (isNewSong) {
          // Defer the swap until the audio for the new song reaches the audience.
          // musicState.progress is how far the host already is, so the remaining
          // wait is the HLS delay minus that.
          const hostPos = hostPositionForNewSong({
            reportedSec: musicState.progress,
            viewerHadSong: Boolean(audienceSongIdRef.current),
          });
          scheduleAudienceSongSwap(incomingSongId, hostPos, (seedSec) => {
            dispatch(
              start({ index: musicState.index, songs: musicState.songs, pos: seedSec, track: null, vol: vol || 25 })
            );
          });
        } else if (!isNewSong && (songsChanged || indexChanged) && pendingSwapSongIdRef.current === null) {
          // Same current song but the list/index changed (reorder/metadata) —
          // reflect it immediately without disturbing the audience timeline, and
          // only when no deferred swap is in flight.
          dispatch(
            start({
              index: musicState.index,
              songs: musicState.songs,
              pos: progressRef.current,
              track: null,
              vol: vol || 25,
            })
          );
        }

      };

      // Direct OBS streams whose playlist is stamped: apply the host's state
      // in force at the moment of the frame on screen. Exact, and immune to
      // the viewer's buffer, so no delay has to be measured.
      // The host's recent states from the server: after a reconnect, and
      // when updates stopped arriving although the clock runs.
      const requestTimeline = () => {
        if (!playHostMusicLocallyRef.current || !socket.connected) return;
        const now = performance.now();
        if (now - lastTimelineRequestAtRef.current < TIMELINE_REQUEST_EVERY_MS) return;
        lastTimelineRequestAtRef.current = now;
        socket.emit('host-music-timeline', { channelName: roomId }, (timeline) => {
          if (!timeline) return;
          lastHostStateAtRef.current = performance.now();
          hostStatesRef.current = mergeHostStates(hostStatesRef.current, hostStatesFromTimeline(timeline));
        });
      };
      socket.on('connect', requestTimeline);

      const followFrameClock = () => {
        if (!playHostMusicLocallyRef.current) return;
        const timing = musicTimingRef.current;
        const frameMs = timing.useFrameClock ? frameClockRef?.current?.ms : null;
        if (typeof frameMs !== "number") {
          if (clockEverActiveRef.current && timing.useFrameClock) {
            // The picture lost its clock (a reload, a recovery): hold the music
            // until frames run again rather than guess with the fixed delay.
            if (syncTargetRef.current) syncTargetRef.current = { ...syncTargetRef.current, stalled: true };
            return;
          }
          clockActiveRef.current = false;
          syncTargetRef.current = null;
          return;
        }
        if (clockEverActiveRef.current && performance.now() - lastHostStateAtRef.current > HOST_STATE_GAP_MS) {
          requestTimeline();
        }
        const motion = clockMotionRef.current;
        if (frameMs !== motion.frameMs) {
          motion.frameMs = frameMs;
          motion.movedAtPerf = performance.now();
        }
        const stalled = performance.now() - motion.movedAtPerf > timing.musicStallMs;
        // A server offset places the music later (positive) or earlier.
        const atMs = frameMs - timing.musicClockOffsetMs;
        const state = hostStateAt(hostStatesRef.current, atMs);
        if (!state) {
          // Nothing the host did covers this frame: play nothing.
          if (clockEverActiveRef.current && scheduledPlayingRef.current !== false) {
            scheduledPlayingRef.current = false;
            setAudiencePlaying(false);
            syncTargetRef.current = null;
          }
          return;
        }
        if (!clockActiveRef.current) {
          clockEverActiveRef.current = true;
          clockActiveRef.current = true;
          cancelPendingSwap();
          playingTimersRef.current.forEach(clearTimeout);
          playingTimersRef.current = [];
        }
        if (state.currentVolume !== null) setHostMusicVolume(state.currentVolume);
        if (scheduledPlayingRef.current !== state.isPlaying) {
          scheduledPlayingRef.current = state.isPlaying;
          setAudiencePlaying(state.isPlaying);
        }
        const song = state.songs?.[state.index];
        if (!song?._id) return;
        const posSec = expectedHostPositionSec(state, atMs);
        const listChanged = songsRef.current.map((s) => s._id).join(",") !== state.songs.map((s) => s._id).join(",");
        if (song._id !== audienceSongIdRef.current) {
          audienceSongIdRef.current = song._id;
          heardStartRef.current = { songId: song._id, startSec: posSec };
          progressRef.current = posSec;
          setProgress(posSec);
          dispatch(start({ index: state.index, songs: state.songs, pos: posSec, track: null, vol: vol || 25 }));
        } else if (listChanged || state.index !== indexRef.current) {
          dispatch(start({ index: state.index, songs: state.songs, pos: progressRef.current, track: null, vol: vol || 25 }));
        }
        syncTargetRef.current = state.isPlaying ? { posSec, atPerfMs: performance.now(), stalled } : null;
      };
      const frameClockTimer = setInterval(followFrameClock, FRAME_CLOCK_TICK_MS);

      socket.on('music-state-update', handleMusicStateUpdate);

      // Handle real-time volume updates from host
      const handleVolumeUpdate = (data) => {
        if (data.roomId !== roomId) return;
        if (remoteAudioTrackRef.current && typeof remoteAudioTrackRef.current.setVolume === 'function') {
          remoteAudioTrackRef.current.setVolume(Math.round(data.volume * 100));
        }
      };

      socket.on('music-volume-update', handleVolumeUpdate);

      return () => {
        socket.off('music-state-update', handleMusicStateUpdate);
        socket.off('music-volume-update', handleVolumeUpdate);
        socket.off('mobile-music-state', handleMobileMusicState);
        cancelPendingSwap();
        playingTimersRef.current.forEach(clearTimeout);
        playingTimersRef.current = [];
        scheduledPlayingRef.current = null;
        clearInterval(frameClockTimer);
        socket.off('connect', requestTimeline);
        clockActiveRef.current = false;
        clockEverActiveRef.current = false;
        syncTargetRef.current = null;
        hostStatesRef.current = [];
      };
    }

    // HOST: Listen for OBS Player play commands
    if (isHost) {
      const handleObsPlaySong = async (data) => {
        if (data.channelName !== roomId) return;

        const { song } = data;
        if (!song || !song._id) return;

        // Get current values - songs might have changed since listener was registered
        const currentSongs = songs;
        const currentTrack = track;
        const currentVol = vol;

        // Find the song index in the current playlist
        const songIndex = currentSongs.findIndex(s => s._id === song._id);

        if (songIndex === -1) return;

        const songToPlay = currentSongs[songIndex];

        // Check if song file exists
        if (!getPlayableUrl(songToPlay)) {
          console.error("[MusicPlayer] No filePath for song:", songToPlay?.title);
          return;
        }

        // Stop current track if playing and emit song-end event
        if (currentTrack && currentTrack.currentState === "playing") {
          try {
            emitSongStartEvent("stop");
            handleStopStreaming();

            // Stop the track
            currentTrack.stopProcessAudioBuffer();
            if (stopMusicTrack) {
              stopMusicTrack();
            }
          } catch (err) {
            console.warn('[MusicPlayer] Error stopping current track:', err);
          }
        }

        try {
          const audioUrl = await currentPlayableUrl(songToPlay);

          if (!publishMusicTrack) return;

          // false = don't publish to Agora or play locally; OBS Player handles both.
          // We still create the track for UI progress tracking (getCurrentTime).
          const newTrack = await publishMusicTrack(audioUrl, currentVol || 80, false);
          if (!newTrack) return; // superseded by a newer load or a stop

          // Emit song-start event for listen count tracking
          emitSongStartEvent("start", songToPlay, 0);
          handleStartStreaming(songToPlay);

          // Update Redux with the new track
          dispatch(start({
            index: songIndex,
            songs: currentSongs,
            pos: 0,
            track: newTrack,
            vol: currentVol || 80,
          }));

        } catch (err) {
          console.error("[MusicPlayer] Error loading song from OBS:", err);
          notifier.error('Failed to load song from OBS. Please try again.');
        }
      };

      const handleObsVolumeChange = (data) => {
        if (data.channelName !== roomId) return;

        const newVolume = Math.round(data.volume * 100);
        const currentTrack = track;

        if (currentTrack && typeof currentTrack.setVolume === 'function') {
          // Scale down music volume (mastered tracks are much louder than raw mic)
          currentTrack.setVolume(Math.round(newVolume * 0.3));
        }

        // Update Redux volume state
        dispatch({
          type: 'UPDATE_VOLUME',
          payload: newVolume,
        });
      };

      socket.on('obs:play-song', handleObsPlaySong);
      socket.on('obs:volume-change', handleObsVolumeChange);

      return () => {
        socket.off('obs:play-song', handleObsPlaySong);
        socket.off('obs:volume-change', handleObsVolumeChange);
        socket.off('mobile-music-state', handleMobileMusicState);
        cancelPendingSwap();
      };
    }
  }, [socket, roomId, isHost, dispatch]);

  /************************************************************************
   * Host broadcasts music state periodically (every 2 seconds when playing)
   ************************************************************************/
  useEffect(() => {
    if (!socket || !roomId || !isHost || !hasSongs) return;

    // Broadcast immediately when state changes
    const broadcastState = () => {
      const musicState = {
        index,
        currentSongId: currentSong?._id,
        progress: progressRef.current,
        duration,
        isPlaying,
        currentVolume: audioVolumeRef.current, // Send real-time volume to audience
        // Waveform samples are large; only the song on air needs them on the audience side
        songs: songs.map((s, songIndex) => ({
          _id: s._id,
          title: s.title,
          artist: s.uploader?.displayArtistName || s.artist,
          thumbnailImg: s.thumbnailImg,
          duration: s.duration || s.fileId?.duration,
          ...(songIndex === index && { sampleData: s.sampleData }),
        }))
      };

      socket.emit('music-state-update', { roomId, musicState });
    };

    // Broadcast immediately when song/playing state changes
    broadcastState();

    // If playing, broadcast every 2 seconds to sync progress
    let intervalId;
    if (isPlaying) {
      intervalId = setInterval(broadcastState, 2000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [socket, roomId, isHost, hasSongs, index, currentSong?._id, isPlaying, duration, songs]);

  /************************************************************************
   * Auto-load playlist songs when component mounts (only if no songs loaded)
   ************************************************************************/
  useEffect(() => {
    // Only load if we have a roomId and no songs currently
    if (!roomId || hasSongs) return;

    const loadPlaylist = async () => {
      try {
        // Use PLAYLIST_DETAIL API (same as song list) instead of GET_PLAYLIST_MUSIC
        const res = await ApiService.PLAYLIST_DETAIL({
          _id: roomId,
          pageNo: 1,
          pageSize: 100,
        });

        const musicArr = res?.result?.music || [];

        if (musicArr.length > 0) {
          dispatch(
            start({
              index: 0,
              songs: musicArr,
              pos: 0,
              track: null,
              vol: vol || 5,
            })
          );
        }
      } catch (error) {
        console.error('[MusicPlayer] Error auto-loading playlist:', error);
      }
    };

    loadPlaylist();
  }, [roomId, hasSongs, dispatch, vol]);

  /************************************************************************
   * (1) Generate waveform bars from actual audio data
   ************************************************************************/
  useEffect(() => {
    const barCount = 50;

    // Always initialize bars if not already set
    if (!barsRef.current) {
      barsRef.current = Array.from({ length: barCount }, () => 0.5);
    }

    if (!currentSong) return;

    // Use real waveform data from sampleData if available
    if (currentSong.sampleData) {
      try {
        let samples;

        // Parse sampleData if it's a string
        if (typeof currentSong.sampleData === 'string') {
          // Remove brackets and split by comma
          const strippedString = currentSong.sampleData.slice(1, -1);
          samples = strippedString.split(',').map(s => parseFloat(s.trim()));
        } else if (Array.isArray(currentSong.sampleData)) {
          samples = currentSong.sampleData;
        } else {
          throw new Error('Invalid sampleData format');
        }

        // Downsample to barCount
        const step = Math.max(1, Math.floor(samples.length / barCount));
        const bars = [];

        for (let i = 0; i < barCount; i++) {
          const sampleIndex = i * step;
          if (sampleIndex < samples.length) {
            // Normalize sample value (sampleData is 0-100, divide by 100 to get 0-1)
            const value = Math.abs(samples[sampleIndex]) / 100;
            bars.push(Math.max(0.2, Math.min(1.0, value)));
          } else {
            bars.push(0.5);
          }
        }

        barsRef.current = bars;
      } catch (error) {
        console.error('[MusicPlayer] Error parsing sampleData:', error);
        // Fallback to default bars
        barsRef.current = Array.from({ length: barCount }, () => 0.5);
      }
    } else {
      barsRef.current = Array.from({ length: barCount }, () => 0.5);
    }
  }, [currentSong]);

  /************************************************************************
   * (1.5) Get real-time audio volume level from track
   * HOST: Uses local BufferSourceAudioTrack
   * AUDIENCE: Uses remote audio track from Agora stream
   ************************************************************************/
  useEffect(() => {
    // For host, use the local track
    if (isHost) {
      if (!isPlaying || !track) {
        audioVolumeRef.current = 0;
        return;
      }

      let frameCount = 0;
      let animFrame;

      const updateVolume = () => {
        frameCount++;

        if (track && typeof track.getVolumeLevel === 'function') {
          // getVolumeLevel returns 0-1 based on actual audio loudness
          const rawVolume = track.getVolumeLevel();

          // Apply noise gate threshold to prevent flickering from ambient noise
          const NOISE_THRESHOLD = 0.05; // Ignore volumes below 5%
          const cleanVolume = rawVolume < NOISE_THRESHOLD ? 0 : rawVolume;

          // Use raw volume with very light smoothing to preserve dynamics
          audioVolumeRef.current = audioVolumeRef.current * 0.1 + cleanVolume * 0.9;

        }

        if (isPlaying) {
          animFrame = requestAnimationFrame(updateVolume);
        }
      };

      updateVolume();

      return () => {
        if (animFrame) {
          cancelAnimationFrame(animFrame);
        }
        audioVolumeRef.current = 0;
      };
    }

    // For audience, find and use the remote audio track
    if (!isHost && remoteUsers && Object.keys(remoteUsers).length > 0) {
      // Find the host's remote audio track
      const hostUser = Object.values(remoteUsers).find(user => user.audioTrack);

      if (!hostUser || !hostUser.audioTrack) {
        audioVolumeRef.current = 0;
        return;
      }

      const remoteAudioTrack = hostUser.audioTrack;
      remoteAudioTrackRef.current = remoteAudioTrack;

      let frameCount = 0;
      let animFrame;

      const updateVolume = () => {
        frameCount++;

        if (remoteAudioTrack && typeof remoteAudioTrack.getVolumeLevel === 'function') {
          // Get volume from remote audio stream
          const rawVolume = remoteAudioTrack.getVolumeLevel();

          // Apply noise gate threshold to prevent flickering from ambient noise
          const NOISE_THRESHOLD = 0.05; // Ignore volumes below 5%
          const cleanVolume = rawVolume < NOISE_THRESHOLD ? 0 : rawVolume;

          // Use raw volume with very light smoothing to preserve dynamics
          audioVolumeRef.current = audioVolumeRef.current * 0.1 + cleanVolume * 0.9;

        }

        if (audiencePlaying) {
          animFrame = requestAnimationFrame(updateVolume);
        }
      };

      updateVolume();

      return () => {
        if (animFrame) {
          cancelAnimationFrame(animFrame);
        }
        audioVolumeRef.current = 0;
      };
    }
  }, [isHost, isPlaying, track, remoteUsers, audiencePlaying]);


  /************************************************************************
   * (2) Waveform Visualization - Draw with real-time audio data
   ************************************************************************/
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !barsRef.current) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const barCount = barsRef.current.length;
    const barWidth = width / barCount;

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Use progressRef to get current progress value
      const currentProgress = progressRef.current;
      const progressRatio = duration > 0 ? currentProgress / duration : 0;
      const activeBarCount = Math.floor(barCount * progressRatio);

      // Get current audio volume level (0-1)
      // Both host and audience use audioVolumeRef which is updated in real-time
      // Host: from local BufferSourceAudioTrack
      // Audience: from remote audio track (Agora stream)
      const currentVolume = audioVolumeRef.current;

      barsRef.current.forEach((barHeight, i) => {
        const x = i * barWidth;
        let h = height * barHeight;

        // Add real-time pulsation based on audio volume
        if (currentVolume > 0 && i < activeBarCount) {
          // Use raw volume for direct response
          // Make bars pulsate: 30% to 130% of original height
          h = h * (0.3 + currentVolume * 1.0);
        }

        const y = (height - h) / 2;

        // Active bars (played portion) with gradient and glow
        if (i < activeBarCount) {
          // Create gradient from yellow -> orange -> pink -> purple
          const gradientPosition = i / activeBarCount;

          let r, g, b;
          if (gradientPosition < 0.33) {
            // Yellow to Orange
            const localPos = gradientPosition / 0.33;
            r = Math.floor(255);
            g = Math.floor(238 - (238 - 100) * localPos);
            b = Math.floor(32);
          } else if (gradientPosition < 0.66) {
            // Orange to Pink
            const localPos = (gradientPosition - 0.33) / 0.33;
            r = Math.floor(255);
            g = Math.floor(100 + (50 - 100) * localPos);
            b = Math.floor(32 + (150 - 32) * localPos);
          } else {
            // Pink to Purple
            const localPos = (gradientPosition - 0.66) / 0.34;
            r = Math.floor(255 - (255 - 150) * localPos);
            g = Math.floor(50);
            b = Math.floor(150 + (255 - 150) * localPos);
          }

          // Add dynamic glow and brightness with controlled range
          // Both host and audience use real-time volume data from their audio tracks
          let brightness = 0.3; // Base brightness at 30%
          let glowIntensity = 0;

          if (currentVolume > 0) {
            // Use raw volume for direct 1:1 response
            // Brightness: 30% to 180% based on volume
            brightness = 0.3 + currentVolume * 1.5;
            // Glow: 0px to 25px based on volume
            glowIntensity = currentVolume * 25;

          }

          // Apply subtle glow when playing (reduced to prevent bleeding)
          if (isPlaying && glowIntensity > 0) {
            ctx.shadowBlur = Math.min(glowIntensity * 0.3, 8); // Reduced to max 8px
            ctx.shadowColor = `rgba(${r}, ${g}, ${b}, 0.6)`;
          } else {
            ctx.shadowBlur = 0;
          }

          // Apply brightness to colors
          const brightR = Math.min(255, Math.floor(r * brightness));
          const brightG = Math.min(255, Math.floor(g * brightness));
          const brightB = Math.min(255, Math.floor(b * brightness));

          ctx.fillStyle = `rgb(${brightR}, ${brightG}, ${brightB})`;
        } else {
          // Inactive bars - no glow
          ctx.shadowBlur = 0;
          ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        }

        // Draw rounded bar
        const barWidthActual = barWidth - 2;
        const radius = Math.min(barWidthActual / 2, 2); // Cap radius at 2px

        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + barWidthActual - radius, y);
        ctx.quadraticCurveTo(x + barWidthActual, y, x + barWidthActual, y + radius);
        ctx.lineTo(x + barWidthActual, y + h - radius);
        ctx.quadraticCurveTo(x + barWidthActual, y + h, x + barWidthActual - radius, y + h);
        ctx.lineTo(x + radius, y + h);
        ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
        ctx.fill();

        // Reset shadow to prevent bleeding to next elements
        ctx.shadowBlur = 0;
      });
    };

    // Always draw the waveform - animate when playing, static when paused
    // Cancel old animation if exists
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    if (hasSongs) {
      if (showAsPlaying || !isHost) {
        // Start animation loop when playing (host) or for audience (always animate to show progress updates)
        const animate = () => {
          draw();
          animationRef.current = requestAnimationFrame(animate);
        };
        animate();
      } else {
        draw();
      }
    }

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [showAsPlaying, hasSongs, isHost, progress]); // Depend on progress too for audience updates

  /************************************************************************
   * (2) Poll track.getCurrentTime => local UI progress + stream tracking
   ************************************************************************/
  useEffect(() => {
    if (!hasTrack) return;

    // Auto-start streaming when track is first detected as playing
    if (isHost && track.currentState === "playing" && currentSong?._id && streamTrackingRef.current.songId !== currentSong._id) {
      streamTrackingRef.current = { started: true, songId: currentSong._id };
      streamingActiveRef.current = currentSong._id;
      ApiService.STREAM_START({
        musicId: currentSong._id,
        playlistId: roomId || undefined,
        livestreamId: roomId || undefined,
      }).catch(err => console.error('[MusicPlayer] STREAM_START failed:', err));
    }

    const intervalId = setInterval(() => {
      if (track.currentState === "playing") {
        const currentTime = track.getCurrentTime();
        setProgress(currentTime);
        progressRef.current = currentTime;

        // Check if song changed (auto-next happened externally)
        const currentSongNow = songsRef.current[indexRef.current];
        if (isHost && currentSongNow?._id && streamTrackingRef.current.songId !== currentSongNow._id) {
          // Stop old
          if (streamTrackingRef.current.songId) {
            ApiService.STREAM_STOP({ musicId: streamTrackingRef.current.songId }).catch(() => {});
          }
          // Start new
          streamTrackingRef.current = { started: true, songId: currentSongNow._id };
          streamingActiveRef.current = currentSongNow._id;
          ApiService.STREAM_START({
            musicId: currentSongNow._id,
            playlistId: roomId || undefined,
            livestreamId: roomId || undefined,
          }).catch(err => console.error('[MusicPlayer] STREAM_START failed:', err));
        }

        if (duration > 0 && currentTime + 1 >= duration) {
          setProgress(duration);
          progressRef.current = duration;
          if (isHost) handleNextSong();
        }
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [hasTrack, track, duration, isHost]);

  /************************************************************************
   * (2.1) Audience local progress interpolation
   * Audience has no local track, so we increment progress every second
   * while audiencePlaying is true. Socket events correct the value.
   ************************************************************************/
  useEffect(() => {
    if (isHost || !audiencePlaying || duration <= 0) return;

    const intervalId = setInterval(() => {
      progressRef.current = Math.min(progressRef.current + 1, duration);
      setProgress(progressRef.current);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isHost, audiencePlaying, duration]);

  /************************************************************************
   * (2.5) Monitor track.currentState for reactive UI updates (play/pause icon)
   * The Agora SDK track.currentState is not reactive, so we poll it
   ************************************************************************/
  useEffect(() => {
    if (!hasTrack || !track) {
      setTrackState(null);
      return;
    }

    let lastSeenState = track.currentState;
    setTrackState(lastSeenState);

    // Poll for state changes (Agora SDK doesn't provide events for state changes)
    const intervalId = setInterval(() => {
      if (track.currentState === lastSeenState) return;
      lastSeenState = track.currentState;
      setTrackState(lastSeenState);
    }, 100);

    return () => clearInterval(intervalId);
  }, [hasTrack, track]);

  /************************************************************************
   * (3) onEnded => auto-next (host only)
   ************************************************************************/
  useEffect(() => {
    if (!hasTrack || !isHost) return;

    track.onEnded = () => {
      if (index < songs.length - 1) {
        handleNextSong();
      } else {
        // Last song ended, emit stop event
        emitSongStartEvent("stop");
        handleStopStreaming();
      }
    };

    return () => {
      if (track) track.onEnded = null;
    };
  }, [hasTrack, track, index, songs, isHost]);

  /************************************************************************
   * (4) Play/Pause (host only) - Load track if needed, then play/pause
   ************************************************************************/
  async function handlePlayPause() {
    if (!isHost || !hasSongs) return;

    // Prevent double-click: if already processing, ignore this click
    if (playPauseInProgress.current) return;

    playPauseInProgress.current = true;

    // If no track loaded, load and play it
    if (!hasTrack && publishMusicTrack) {
      const song = songs[displayIndex];
      if (!getPlayableUrl(song)) {
        console.error("[MusicPlayer] No filePath for song:", song?.title);
        playPauseInProgress.current = false;
        return;
      }

      try {
        const audioUrl = await currentPlayableUrl(song);
        setIsLoading(true);

        // publishMusicTrack owns the timeout. A caller-side race here used to
        // abandon the load, which then finished, started playing and
        // published on its own with nothing in the UI to stop it.
        const newTrack = await publishMusicTrack(audioUrl, vol || 25, true);
        if (!newTrack) return; // superseded by a newer load or a stop

        // Update Redux with the new track
        dispatch(
          start({
            index: displayIndex,
            songs,
            pos: 0,
            track: newTrack,
            vol: vol || 25,
          })
        );

        // Emit start event for listen count tracking (first play of this song)
        emitSongStartEvent("start", song, 0);
        handleStartStreaming(song);
      } catch (err) {
        console.error("[MusicPlayer] Error loading song:", err);
        notifier.error('Failed to load song. Please try another song.');
      } finally {
        setIsLoading(false);
        playPauseInProgress.current = false;
      }
      return;
    }

    // If track exists, control playback
    if (hasTrack) {
      try {
        if (track.currentState === "playing") {
          track.pauseProcessAudioBuffer();
          track.setEnabled(false);
          // Emit pause event for listen count tracking
          emitSongStartEvent("pause");
          handleStopStreaming();
        } else if (track.currentState === "paused") {
          track.setEnabled(true);
          track.resumeProcessAudioBuffer();
          // Emit start event for listen count tracking (resuming)
          emitSongStartEvent("start");
          handleStartStreaming(currentSong);
        } else {
          track.startProcessAudioBuffer();
          // Emit start event for listen count tracking
          emitSongStartEvent("start");
          handleStartStreaming(currentSong);
        }
      } catch (err) {
        console.error("[MusicPlayer] Error controlling playback:", err);
        // If pause fails, the track might not be ready yet - ignore
      } finally {
        playPauseInProgress.current = false;
      }
    } else {
      playPauseInProgress.current = false;
    }
  }

  /************************************************************************
   * (5) Next/Previous Song (host only) - Auto-play if currently playing
   ************************************************************************/
  async function handlePrevSong() {
    if (!isHost || !hasSongs || displayIndex <= 0) return;
    if (playPauseInProgress.current) return;
    playPauseInProgress.current = true;

    const newIndex = displayIndex - 1;
    const wasPlaying = isPlaying;

    try {
      // Emit stop event for current song before switching
      if (wasPlaying) {
        emitSongStartEvent("stop");
        handleStopStreaming();
      }

      // Stop current track if playing
      if (hasTrack && stopMusicTrack) {
        await stopMusicTrack();
      }

      // If something was playing, load and play the new track
      if (wasPlaying && publishMusicTrack) {
        const song = songs[newIndex];
        if (!getPlayableUrl(song)) {
          console.error("[MusicPlayer] No filePath for song:", song?.title);
          return;
        }

        const audioUrl = await currentPlayableUrl(song);
        const newTrack = await publishMusicTrack(audioUrl, vol || 25, true);
        if (!newTrack) return; // superseded by a newer load or a stop

        dispatch(
          start({
            index: newIndex,
            songs,
            pos: 0,
            track: newTrack,
            vol: vol || 25,
          })
        );

        // Emit previous event for the new song starting
        emitSongStartEvent("previous", song, 0);
        handleStartStreaming(song);
      } else {
        // Just update the index, don't load/play the track
        dispatch(
          start({
            index: newIndex,
            songs,
            pos: 0,
            track: null,
            vol: vol || 25,
          })
        );
      }
    } catch (err) {
      console.error("[MusicPlayer] Error loading previous song:", err);
      dispatch(
        start({
          index: newIndex,
          songs,
          pos: 0,
          track: null,
          vol: vol || 25,
        })
      );
    } finally {
      playPauseInProgress.current = false;
    }
  }

  async function handleNextSong() {
    if (!isHost || !hasSongs || displayIndex >= songs.length - 1 || isLoading) return;
    // Prevent concurrent calls (from progress polling + onEnded firing simultaneously)
    if (nextSongInProgress.current) return;
    nextSongInProgress.current = true;

    const newIndex = displayIndex + 1;
    const wasPlaying = isPlaying;

    // Emit stop event for current song before switching
    if (wasPlaying) {
      emitSongStartEvent("stop");
      handleStopStreaming();
    }

    // Stop current track if playing
    if (hasTrack && stopMusicTrack) {
      await stopMusicTrack();
    }

    // If something was playing, load and play the new track
    if (wasPlaying && publishMusicTrack) {
      const song = songs[newIndex];
      if (!getPlayableUrl(song)) {
        console.error("[MusicPlayer] No filePath for song:", song?.title);
        return;
      }

      setIsLoading(true);

      try {
        const audioUrl = await currentPlayableUrl(song);
        const newTrack = await publishMusicTrack(audioUrl, vol || 25, true);
        setIsLoading(false);
        if (!newTrack) {
          nextSongInProgress.current = false;
          return; // superseded by a newer load or a stop
        }

        dispatch(
          start({
            index: newIndex,
            songs,
            pos: 0,
            track: newTrack,
            vol: vol || 25,
          })
        );
        // Emit next event for the new song starting
        emitSongStartEvent("next", song, 0);
        handleStartStreaming(song);
      } catch (err) {
        console.error("[MusicPlayer] Error loading next song:", err);
        setIsLoading(false);
        nextSongInProgress.current = false;

        // If loading fails, just update index without track (user can try play again)
        dispatch(
          start({
            index: newIndex,
            songs,
            pos: 0,
            track: null,
            vol: vol || 25,
          })
        );
      }
    } else {
      // Just update the index, don't load/play the track
      dispatch(
        start({
          index: newIndex,
          songs,
          pos: 0,
          track: null,
          vol: vol || 25,
        })
      );
    }
    nextSongInProgress.current = false;
  }

  /************************************************************************
   * (6) Format time helper
   ************************************************************************/
  function formatTime(seconds) {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /************************************************************************
   * (7) Tip Song Handler
   * 75% goes to song artist, 25% goes to livestream host
   ************************************************************************/
  const handleTipSong = () => {
    if (!currentSong) return;

    // Get the artist/uploader info from the song
    const artist = currentSong.uploader || {
      _id: currentSong.creatorId || currentSong.owner,
      displayArtistName: currentSong.artist || "Unknown Artist"
    };

    // Get host info from streamData
    const hostId = streamData?.createdByDetails?._id || streamData?.createdBy || streamData?.owner;

    // Emit event to open TipPopup for livestream song tip
    // Backend will split: 75% to artist, 25% to host
    emitter.emit("openTipModal", {
      user: artist,
      songId: currentSong._id,
      hostId: hostId,
      livestreamId: roomId,
      tipType: "livestreamSong"
    });
  };

  /************************************************************************
   * (8) Save to Playlist Handler
   ************************************************************************/
  const handleSaveToPlaylist = () => {
    if (!currentSong) return;
    setShowPlaylistDialog(true);
  };

  // Show song info if we have songs, even without a track
  const showSongInfo = currentSong !== null;

  /** Pick a song from the song picker panel */
  const handlePickSong = async (songIndex) => {
    if (!isHost || !hasSongs || songIndex < 0 || songIndex >= songs.length) return;
    if (songIndex === displayIndex && isPlaying) return;
    if (playPauseInProgress.current) return;
    playPauseInProgress.current = true;

    const wasPlaying = isPlaying;
    const song = songs[songIndex];

    try {
      if (wasPlaying) {
        emitSongStartEvent("stop");
        handleStopStreaming();
      }
      if (hasTrack && stopMusicTrack) {
        await stopMusicTrack();
      }

      if (publishMusicTrack && getPlayableUrl(song)) {
        setIsLoading(true);
        const newTrack = await publishMusicTrack(await currentPlayableUrl(song), vol || 25, true);
        if (!newTrack) return; // superseded by a newer load or a stop
        dispatch(start({ index: songIndex, songs, pos: 0, track: newTrack, vol: vol || 25 }));
        emitSongStartEvent("start", song, 0);
        handleStartStreaming(song);
        setShowSongPicker(false);
      } else {
        dispatch(start({ index: songIndex, songs, pos: 0, track: null, vol: vol || 25 }));
      }
    } catch (err) {
      console.error('[MusicPlayer] Error loading picked song:', err);
      notifier.error('Failed to load song. Please try another song.');
      dispatch(start({ index: songIndex, songs, pos: 0, track: null, vol: vol || 25 }));
    } finally {
      setIsLoading(false);
      playPauseInProgress.current = false;
    }
  };
  controlHandlersRef.current.handlePickSong = handlePickSong;

  return (
    <Box sx={{ maxWidth: "640px", width: "100%", position: "relative", zIndex: 35 }}>
      {/* Song Picker Panel — overlays above the player with slide animation */}
      {canDrive && hasSongs && (
        <Box
          sx={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            right: 0,
            backgroundColor: "rgba(20, 20, 20, 0.97)",
            backdropFilter: "blur(12px)",
            borderRadius: "8px 8px 0 0",
            maxHeight: showSongPicker ? "240px" : "0px",
            opacity: showSongPicker ? 1 : 0,
            overflowY: showSongPicker ? "auto" : "hidden",
            borderBottom: showSongPicker ? "1px solid rgba(255,255,255,0.08)" : "none",
            boxShadow: showSongPicker ? "0 -4px 16px rgba(0,0,0,0.4)" : "none",
            transition: "max-height 0.3s ease-in-out, opacity 0.25s ease-in-out",
          }}
        >
          {songs.reduce((acc, song, i) => {
            if (!acc.seen.has(song._id)) {
              acc.seen.add(song._id);
              acc.items.push(
                <Box
                  key={song._id || i}
                  onClick={() => (isHost ? handlePickSong(i) : sendControl('pick', { index: i }))}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    padding: "8px 14px",
                    cursor: "pointer",
                    backgroundColor: i === displayIndex ? "rgba(255,255,255,0.08)" : "transparent",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.06)" },
                    borderLeft: i === displayIndex ? "3px solid #4CAF50" : "3px solid transparent",
                  }}
                >
                  <Box
                    component="img"
                    src={artworkUrlOf(song) || ARTWORK_FALLBACK}
                    onError={showArtworkFallback}
                    alt=""
                    sx={{ width: 32, height: 32, borderRadius: "3px", objectFit: "cover", flexShrink: 0 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: i === displayIndex ? 700 : 500, color: i === displayIndex ? "#4CAF50" : "rgba(255,255,255,0.85)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {song.title || "Unknown"}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {song.uploader?.displayArtistName || song.artist || "Unknown Artist"}
                    </Typography>
                  </Box>
                  {i === displayIndex && showAsPlaying && (
                    <Box sx={{ width: 14, height: 14, flexShrink: 0, display: "flex", alignItems: "center", gap: "1px" }}>
                      {[0, 1, 2].map(b => (
                        <Box key={b} sx={{ width: "3px", backgroundColor: "#4CAF50", borderRadius: "1px", animation: "eqBounce 0.6s ease-in-out infinite alternate", animationDelay: `${b * 0.15}s`, height: "60%", "@keyframes eqBounce": { "0%": { height: "30%" }, "100%": { height: "100%" } } }} />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            }
            return acc;
          }, { seen: new Set(), items: [] }).items}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
        }}
      >
      {/* Up/Down arrow to toggle song picker — whoever may drive the music */}
      {canDrive && hasSongs && (
        <IconButton
          aria-label="Toggle song list"
          onClick={() => setShowSongPicker(!showSongPicker)}
          size="small"
          sx={{
            color: showSongPicker ? "#4CAF50" : "rgba(255, 255, 255, 0.6)",
            padding: "4px",
            flexShrink: 0,
            "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)", color: "#fff" },
          }}
        >
          {showSongPicker
            ? <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20 }} />
            : <KeyboardArrowUpRoundedIcon sx={{ fontSize: 20 }} />
          }
        </IconButton>
      )}

      {!showSongInfo ? (
        // No songs available at all
        <Box sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "8px 0" }}>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255, 255, 255, 0.5)",
              fontSize: "12px",
            }}
          >
            {isHost ? "Add songs to your playlist to start" : "Waiting for host..."}
          </Typography>
        </Box>
      ) : (
        <>
          {/* Song Cover */}
          <Box
            component="img"
            src={artworkUrlOf(currentSong) || ARTWORK_FALLBACK}
                    onError={showArtworkFallback}
            alt="Album cover"
            sx={{
              width: 44,
              height: 44,
              borderRadius: "6px",
              objectFit: "cover",
              flexShrink: 0
            }}
          />

          {/* Song Title and Artist - Stacked */}
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: "80px", flexShrink: 1, overflow: "hidden" }}>
            <Typography
              variant="caption"
              sx={{
                color: "#fff",
                fontSize: "13px",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.3
              }}
            >
              {currentSong?.title || "Unknown Track"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "var(--text-muted, #6A6A80)",
                fontSize: "12px",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: 1.3
              }}
            >
              {currentSong?.uploader?.displayArtistName || currentSong?.artist || "Unknown Artist"}
            </Typography>
          </Box>

          {/* Tip and Save to Playlist Buttons - For all users */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Tip Song" arrow>
              <IconButton
                aria-label="Tip song"
                onClick={handleTipSong}
                size="small"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(76, 175, 80, 0.2)",
                    color: "#4CAF50"
                  }
                }}
              >
                <AttachMoneyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Save to Playlist" arrow>
              <IconButton
                aria-label="Save to playlist"
                onClick={handleSaveToPlaylist}
                size="small"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  padding: "4px",
                  "&:hover": {
                    backgroundColor: "rgba(100, 150, 255, 0.2)",
                    color: "#6496ff"
                  }
                }}
              >
                <PlaylistAddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Control Buttons - host, or a moderator driving the host's player */}
          {(isHost || canControl) && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <IconButton
                aria-label="Previous track"
                onClick={isHost ? handlePrevSong : () => sendControl('prev')}
                disabled={index === 0}
                size="small"
                sx={{
                  color: index === 0 ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.8)",
                  padding: "4px",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" }
                }}
              >
                <SkipPreviousRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>

              <IconButton
                aria-label={isLoading ? "Loading song" : "Play/Pause"}
                onClick={isHost ? handlePlayPause : () => sendControl('toggle')}
                disabled={isHost && isLoading}
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  color: "#000",
                  padding: "7px",
                  "&:hover": {
                    backgroundColor: "#fff",
                    transform: "scale(1.05)"
                  },
                  transition: "all 0.2s"
                }}
              >
                {isHost && isLoading ? (
                  <CircularProgress size={18} thickness={5} sx={{ color: "#000" }} />
                ) : showAsPlaying ? (
                  <PauseRoundedIcon sx={{ fontSize: 18 }} />
                ) : (
                  <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
                )}
              </IconButton>

              <IconButton
                aria-label="Next track"
                onClick={isHost ? handleNextSong : () => sendControl('next')}
                disabled={index >= songs.length - 1}
                size="small"
                sx={{
                  color: index >= songs.length - 1 ? "rgba(255, 255, 255, 0.3)" : "rgba(255, 255, 255, 0.8)",
                  padding: "4px",
                  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.1)" }
                }}
              >
                <SkipNextRoundedIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          )}

          {/* Waveform Visualization - visible to all, scrubbing only for host */}
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", minWidth: 0, gap: 0.5, overflow: "hidden" }}>
            <canvas
              ref={canvasRef}
              width={400}
              height={28}
              style={{
                flex: 1,
                minWidth: 0,
                height: "28px",
                cursor: isHost ? "pointer" : "default",
              }}
              onClick={(e) => {
                // Only host can scrub
                if (!isHost || !hasTrack) return;
                const rect = e.currentTarget.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const ratio = x / rect.width;
                const newPos = duration * ratio;

                if (typeof track.seekAudioBuffer === "function") {
                  track.seekAudioBuffer(newPos);
                }
                setProgress(newPos);
                progressRef.current = newPos; // Update ref for canvas access

                dispatch(
                  start({
                    index,
                    songs,
                    pos: newPos,
                    track,
                    vol: vol,
                  })
                );
              }}
            />
            {/* Time Display - visible to both host and viewers */}
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.7)",
                fontSize: "11px",
                fontFamily: "var(--font-mono)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                textAlign: "right"
              }}
            >
              {formatTime(isHost ? progress : progressRef.current)} / {formatTime(duration)}
            </Typography>
          </Box>
        </>
      )}

      {/* Add to Playlist Dialog */}
      {showPlaylistDialog && currentSong && (
        <AddMusicToPlaylist
          musicId={currentSong._id}
          open={showPlaylistDialog}
          handleClose={() => setShowPlaylistDialog(false)}
        />
      )}
    </Box>
    </Box>
  );
}
